import type { Worksheet } from "exceljs";
import { COLUMN_HEADER_FILL, THIN_BORDER } from "../../dailyPrintout/render/cellStyle";
import {
	DEPARTMENT_FONT_SIZE,
	DEPARTMENT_ROW,
	FIRST_BLOCK_ROW,
	GENERATED_FONT_SIZE,
	GENERATED_ROW,
	SUBTITLE_FONT_SIZE,
	SUBTITLE_ROW,
	TITLE_FONT_SIZE,
	TITLE_ROW,
	WEEK_COLUMN_COUNT,
	WEEKDAY_HEADER_ROW,
} from "../constants";
import type { WeekStaffScheduleExportColors, WeekStaffScheduleExportLabels } from "../types";

type RenderExportHeaderArgs = {
	worksheet: Worksheet;
	labels: WeekStaffScheduleExportLabels;
	colors: WeekStaffScheduleExportColors;
};

const LAST_COLUMN_LETTER = "G";

/** One full-width, centered line of the header block. */
const writeCenteredBanner = ({ worksheet, row, value, size, argb }: { worksheet: Worksheet; row: number; value: string; size: number; argb: string }): void => {
	worksheet.mergeCells(`A${row}:${LAST_COLUMN_LETTER}${row}`);
	const cell = worksheet.getCell(row, 1);
	cell.value = value;
	cell.font = { size, color: { argb } };
	cell.alignment = { horizontal: "center", vertical: "middle" };
};

/**
 * Employee, department, range covered, when it was generated, then the weekday names. Returns the
 * first row a week block may use.
 *
 * Weekday labels arrive already rotated to the visible week start, and spelled out in full — a
 * spreadsheet column is wide enough for the whole name, so the grid's single-letter labels would
 * read as a defect on paper.
 */
export const renderExportHeader = ({ worksheet, labels, colors }: RenderExportHeaderArgs): number => {
	worksheet.mergeCells(`A${TITLE_ROW}:${LAST_COLUMN_LETTER}${TITLE_ROW}`);
	const titleCell = worksheet.getCell(TITLE_ROW, 1);
	titleCell.value = labels.title;
	titleCell.font = { bold: true, size: TITLE_FONT_SIZE };
	titleCell.alignment = { horizontal: "center", vertical: "middle" };

	writeCenteredBanner({ worksheet, row: DEPARTMENT_ROW, value: labels.department, size: DEPARTMENT_FONT_SIZE, argb: colors.dateRowFontArgb });
	writeCenteredBanner({ worksheet, row: SUBTITLE_ROW, value: labels.subtitle, size: SUBTITLE_FONT_SIZE, argb: colors.subtitleFontArgb });
	writeCenteredBanner({ worksheet, row: GENERATED_ROW, value: labels.generatedAt, size: GENERATED_FONT_SIZE, argb: colors.subtitleFontArgb });

	for (let column = 0; column < WEEK_COLUMN_COUNT; column++) {
		const cell = worksheet.getCell(WEEKDAY_HEADER_ROW, column + 1);
		cell.value = labels.weekDayHeaders[column];
		cell.font = { bold: true };
		cell.fill = COLUMN_HEADER_FILL;
		cell.border = THIN_BORDER;
		cell.alignment = { horizontal: "center", vertical: "middle" };
	}

	return FIRST_BLOCK_ROW;
};
